<script lang="ts">

  import { page } from "$app/state"
  import httpClient from "$lib/axios-client"
  import { Button } from "$lib/components/ui/button"
  import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "$lib/components/ui/command"
  import { Input } from "$lib/components/ui/input"
  import { Label } from "$lib/components/ui/label"
  import { Popover, PopoverButton, PopoverContent, PopoverTrigger } from "$lib/components/ui/popover"
  import { RelationEmpty } from "$lib/data/schema/_relation"
  import { type Relation, type RelationData, RelationDataEmpty } from "$lib/data/type/_relation"
  import type { _response } from "$lib/data/type/_response"
  import { createFilter } from "$lib/hooks/filter.svelte"
  import { createSearch } from "$lib/hooks/search.svelte"
  import { useDialog } from "$lib/hooks/use-dialog.svelte"
  import FilterIcon from "@lucide/svelte/icons/filter"
  import LoaderCircleIcon from "@lucide/svelte/icons/loader-circle"
  import RefreshCwIcon from "@lucide/svelte/icons/refresh-cw"
  import { createInfiniteQuery, createQuery } from "@tanstack/svelte-query"
  import DialogProductDetail from "./_components/dialog-product-detail.svelte"
  import ProductCard from "./_components/product-card.svelte"
  import Navigation from "./../_components/navigation.svelte"
  import { type Product, ProductEmpty } from "./_types"

  const pageSize = 20

  const prefix = $derived(page.params.prefix ?? "0")
  const initialCustomerId = page.url.searchParams.get("customer_id") ?? ""

  let bottomOfPage = $state<HTMLDivElement>()

  const filter = createFilter<{
    search: string,
    customer: RelationData
    product_group: Relation
  }>({
    search: "",
    customer: {
      id: initialCustomerId,
      name: "",
      order: 0,
    },
    product_group: RelationEmpty,
  })

  let select = $state<Product>(ProductEmpty)
  const detailDialog = useDialog()

  const customerSearch = createSearch(initialCustomerId)
  const customers = createQuery(() => ({
    queryKey: ["master-customer", {
      prefix,
      customerSearch: customerSearch.debounced,
    }],
    placeholderData: (previous) => previous,
    queryFn: async () => {

      const response = await httpClient.get<_response<RelationData[]>>(`/api/${prefix}/products/master-customer`, {
          params: {
            search: customerSearch.debounced,
          },
        },
      )
      const responseData = response.data.result ?? []

      if (
        filter.state.customer.id !== "" &&
        filter.state.customer.name === ""
      ) {
        filter.set(
          "customer",
          responseData.find((item) => item.id === filter.state.customer.id) ?? RelationDataEmpty
        )
      }

      return responseData
    },
  }))
  const productGroups = createQuery(() => ({
    queryKey: ["master-product-group", prefix],
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/products/master-product-group`)
      return response.data.result ?? []
    },
  }))

  const products = createInfiniteQuery(() => ({
    queryKey: ["product", {
      prefix,
      filter
    }],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {

      const response = await httpClient.get<_response<Product[]>>(`/api/${prefix}/products`, {
        params: {
          page: pageParam,
          limit: pageSize,
          customer_id: filter.state.customer.id,
          group_id: filter.state.product_group.id,
          search: filter.state.search,
        },
      })

      return response.data.result ?? []
    },
    getNextPageParam: (last, pages) => (last.length === 0 ? undefined : pages.length),
  }))


  $effect(() => {
    if (!bottomOfPage || !products.hasNextPage || products.isFetching) return

    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) void products.fetchNextPage()
    }, { threshold: 0.5 })

    observer.observe(bottomOfPage)
    return () => observer.disconnect()
  })

</script>

<main class="overflow-auto relative mx-auto h-full max-w-3xl">
  <nav class="sticky w-full top-0 inset-s-0 inset-e-0 z-10 bg-background border-b max-w-3xl">
    <div class="flex h-12 items-center justify-between px-3">
      <div class="flex items-center">
        <h1 class="text-lg font-medium">
          Produk
        </h1>
      </div>
      <div></div>
    </div>
    <div class="flex gap-2 px-3 py-2">
      <Button
        variant="outline"
        size="icon"
        class="group shrink-0"
        data-loading={products.isFetching}
        disabled={products.isFetching}
        onclick={() => {
            products.refetch()
          }}
      >
        {#if products.isFetching}
          <LoaderCircleIcon class="animate-spin" aria-hidden="true"/>
        {:else}
          <RefreshCwIcon aria-hidden="true"/>
        {/if}
      </Button>
      <Input
        type="text"
        class="min-w-0 flex-1"
        autocapitalize="off"
        placeholder="Cari..."
        value={filter.state.search}
        oninput={(e) => filter.set("search", e.currentTarget.value)}
      />
      <div class="flex-1"></div>
      <Popover>
        <PopoverTrigger>
          {#snippet child({ props })}
            <Button
              variant="outline"
              size="icon"
              aria-label="Filter produk"
              {...props}
            >
              <FilterIcon aria-hidden="true"/>
            </Button>
          {/snippet}
        </PopoverTrigger>
        <PopoverContent
          align="end"
          class="relative grid w-72 gap-4 p-4"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <div class="space-y-1">
            <Label>Customer</Label>
            <Popover>
              <PopoverButton
                class="w-full min-w-0"
                value={filter.state.customer.name || "Semua Customer"}
              />
              <PopoverContent>
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Cari..."
                    value={customerSearch.state}
                    oninput={(e) => customerSearch.set(e.currentTarget.value)}
                  />
                  <CommandList class="min-h-20">
                    <CommandEmpty>Tidak ada data</CommandEmpty>
                    <CommandGroup>
                      {#each customers.data ?? [] as item (item.id)}
                        <CommandItem
                          value={item.name}
                          onclick={() => {
                              filter.set("customer", item)
                              document.keyEsc()
                            }}
                        >
                          {item.name}
                        </CommandItem>
                      {/each}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div class="space-y-1">
            <Label>Group Produk</Label>
            <Popover>
              <PopoverButton
                class="w-full min-w-0"
                value={filter.state.product_group.name || "Semua Group"}
              />
              <PopoverContent>
                <Command>
                  <CommandList class="min-h-20">
                    <CommandEmpty>Tidak ada data</CommandEmpty>
                    <CommandGroup>
                      {#each productGroups.data ?? [] as item (item.id)}
                        <CommandItem
                          value={item.name}
                          onclick={() => {
                              filter.set("product_group", item)
                              document.keyEsc()
                            }}
                        >
                          {item.name}
                        </CommandItem>
                      {/each}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  </nav>
  <div class="min-h-full">
    {#if !products.isFetching && !products.isError && products.data?.pages[0].isEmpty()}
      <div class="absolute left-0 top-0 flex h-full w-full items-center justify-center">
        Tidak ada data
      </div>
    {:else if !products.isFetching && products.isError}
      <div class="absolute left-0 top-0 flex h-full w-full items-center justify-center">
        <div class="flex w-60 flex-col space-y-4 text-center">
          <div>
            <p class="text-sm text-muted-foreground">
              Terjadi kesalahan, Silahkan coba sesaat lagi
            </p>
          </div>
          <div>
            <Button
              variant="outline"
              class="h-8 rounded-full"
              onclick={() => void products.refetch()}
            >
              Muat Ulang
            </Button>
          </div>
        </div>
      </div>
    {/if}
    <div class="h-3"></div>
    <div class="grid grid-cols-2 gap-3 px-3">
      {#each products.data?.pages as items}
        {#each items as item}
          <ProductCard
            product={item}
            onSelect={(value) => {
              select = value
              detailDialog.setOpen(true)
            }}/>
        {/each}
      {/each}
    </div>
    <div class="h-16"></div>
  </div>
  <div bind:this={bottomOfPage} class="h-1"></div>
  <Navigation/>
</main>
<DialogProductDetail
  product={select}
  open={detailDialog.state}
  setOpen={detailDialog.setOpen}
/>
